/*
 * tripSync.js — background synchronization for the offline-first itinerary page.
 *
 * Sits on top of offlineStore. Conflict policy is "local pending wins" (LWW):
 * on reconnect we replay the queued local mutations to the server BEFORE re-reading
 * fresh data, so the server ends up reflecting local intent.
 *
 * The core offline edits only touch existing server IDs (delete / reorder / pin /
 * rename city / trip dates), so the outbox is a plain FIFO replay of requests with
 * no temp-ID reconciliation.
 */

import { enqueue, listOutbox, dequeue } from './offlineStore';

export function isOnline() {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

function authHeaders(extra) {
  const token = localStorage.getItem('token');
  const headers = { ...(extra || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function sendOp(op) {
  const opts = { method: op.method, headers: authHeaders() };
  if (op.body != null) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = typeof op.body === 'string' ? op.body : JSON.stringify(op.body);
  }
  return fetch(op.url, opts);
}

/**
 * Replay all queued ops for a trip in FIFO order.
 * - On a completed HTTP response (even a 4xx/5xx) we drop the op: the request
 *   reached the server, so retrying would double-apply. Network failures instead
 *   stop the flush and keep the remaining ops for the next reconnect.
 * Returns { flushed, remaining }.
 */
export async function flushOutbox(tripId) {
  if (!isOnline()) {
    const pending = await listOutbox(tripId);
    return { flushed: 0, remaining: pending.length };
  }
  const ops = await listOutbox(tripId);
  let flushed = 0;
  for (const op of ops) {
    try {
      await sendOp(op);        // resolved = request reached the server
      await dequeue(op.id);
      flushed++;
    } catch (_) {
      // Network error — stop; preserve order for the next attempt.
      break;
    }
  }
  const remaining = await listOutbox(tripId);
  return { flushed, remaining: remaining.length };
}

/**
 * Perform a mutation. When online, send immediately and fall back to the outbox
 * only on network failure. When offline, queue right away.
 * Returns { sent: true } or { queued: true }.
 */
export async function queueOrSend({ tripId, method, url, body }) {
  const op = { tripId, method, url, body };
  if (isOnline()) {
    try {
      await sendOp(op);
      return { sent: true };
    } catch (_) {
      await enqueue(op);
      return { queued: true };
    }
  }
  await enqueue(op);
  return { queued: true };
}

/**
 * Subscribe to browser reconnect events. Returns an unsubscribe function.
 */
export function onReconnect(cb) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => { try { cb(); } catch (_) {} };
  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}
